<script setup>

import OrganisationLayout from "@/layouts/OrganisationLayout.vue";
import {computed, onMounted, ref} from "vue";
import {GetOrganisation} from "@/services/organisationService.js";
import {useRoute, useRouter} from "vue-router";
import {Skeleton} from "@/components/ui/skeleton/index.js";
import {Plus, ChevronRight} from "lucide-vue-next";
import {Button} from "@/components/ui/button/index.js";
import {Card, CardContent, CardDescription, CardHeader, CardTitle} from "@/components/ui/card/index.js";
import {GetApps} from "@/services/appService.js";
import {getContext} from "@/services/context.js";
import {getViewMode} from "@/services/viewModeService.js";
import ViewModePicker from "@/components/ViewModePicker.vue";

const route = useRoute();
const router = useRouter();

const organisation = ref(null);
const apps = ref([]);
const isLoading = ref(true);
const hasApps = computed(() => apps.value.length > 0);
const viewMode = ref(getViewMode());

onMounted(async () => {
  try {
    isLoading.value = true;
    organisation.value = await GetOrganisation(getContext(route));
    apps.value = await GetApps(getContext(route));
  } catch (error) {
    console.error('Failed to load data:', error);
  } finally {
    isLoading.value = false;
  }
});

function gotoCreate() {
  router.push({path: `/${route.params.organisationId}/create`});
}

function goToApp(app) {
  router.push({path: `/${route.params.organisationId}/app/${app.id}`});
}

function handleViewModeChange(mode) {
  viewMode.value = mode;
}

</script>

<template>
  <OrganisationLayout :organisation="organisation">
    <div class="flex-1 space-y-4">
      <!-- Toolbar -->
      <div class="flex items-center justify-between space-y-2 bg-gray-100 p-4 rounded-md">
        <div class="flex items-center space-x-2">
          <Button @click="gotoCreate" v-if="!isLoading">
            <Plus/>
            New app
          </Button>
          <Skeleton v-if="isLoading" class="w-24 h-8"/>
        </div>
        <ViewModePicker v-model="viewMode" />
      </div>

      <!-- Loading State -->
      <div class="grid gap-4 md:grid-cols-2 lg:grid-cols-4" v-if="isLoading">
        <div v-for="index in 3" :key="index" class="w-full">
          <Card>
            <CardHeader>
              <CardTitle>
                <Skeleton class="h-16"/>
              </CardTitle>
              <CardDescription>
                <Skeleton class="h-4"/>
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Skeleton class="h-4 w-24 mt-2"/>
            </CardContent>
          </Card>
        </div>
      </div>

      <!-- Grid View -->
      <div v-if="hasApps && !isLoading && viewMode === 'grid'">
        <div class="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <div v-for="app in apps" :key="app.id" class="w-full">
            <Card>
              <CardHeader>
                <CardTitle>
                  <span class="pe-2">
                    <i class="fa-brands fa-apple" v-if="app.platform === 0"></i>
                    <i class="fa-brands fa-android" v-if="app.platform === 1"></i>
                  </span>
                  {{ app.name }}
                </CardTitle>
                <CardDescription>{{ app.description }}</CardDescription>
              </CardHeader>
              <CardContent>
                <Button @click="goToApp(app)">
                  Open
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <!-- List View -->
      <div v-if="hasApps && !isLoading && viewMode === 'list'" class="space-y-2">
        <Card v-for="app in apps" :key="app.id">
          <CardContent class="p-4">
            <div class="flex items-center justify-between">
              <div class="space-y-1">
                <h3 class="font-semibold">
                  <span class="pe-2">
                    <i class="fa-brands fa-apple" v-if="app.platform === 0"></i>
                    <i class="fa-brands fa-android" v-if="app.platform === 1"></i>
                  </span>
                  {{ app.name }}
                </h3>
                <p class="text-sm text-muted-foreground">{{ app.description }}</p>
              </div>
              <Button @click="goToApp(app)" variant="ghost" size="sm">
                Open
                <ChevronRight />
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      <!-- Empty State -->
      <div v-if="!hasApps && !isLoading">
        <Card class="w-full mx-auto">
          <CardHeader>
            <CardTitle class="text-center">It looks like you don't have any apps yet</CardTitle>
            <CardDescription class="text-center mt-8">Getting started with PanelPop is easy</CardDescription>
          </CardHeader>
          <CardContent class="text-center">
            <Button @click="gotoCreate">
              <Plus/>
              Create one now
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  </OrganisationLayout>
</template>

<style scoped>

</style>